import { ConnectButton } from "@/components/connect-button";
import { ApplicationLink, Link } from "@/components/links";
import {
  TransactionDialog,
  transactionInner,
} from "@/components/transaction/transaction-dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAlgoAcctInfo, useBalance } from "@/hooks/use-algo-account";
import { clearCStateCache } from "@/hooks/use-cstate";
import { useHistoryEvents } from "@/hooks/use-history-events";
import { makeRedeemTransactions } from "@/lib/clients";
import { getHighlightClasses } from "@/lib/get-tokens-highlights";
import { DSContractListingExtd } from "@/lib/types";
import { cn, formatNumber, shorten } from "@/lib/utils";
import { DSContractState } from "@myth-finance/dualstake-ts-sdk";
import { useWallet } from "@txnlab/use-wallet";
import { cx } from "class-variance-authority";
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  HandCoins,
  Sunset,
  UserRound,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

interface RedeemFormProps {
  listing: DSContractListingExtd;
  cState: DSContractState;
}

export function RedeemForm({ listing, cState }: RedeemFormProps) {
  const { rate, lstId, lstName, asaUnitName, asaDecimals } = listing;
  const { refetch: refetchHistoryEvents } = useHistoryEvents(cState.appId);
  const { activeAddress } = useWallet();

  const [amount, setAmount] = useState(0);
  const [asaAmount, setAsaAmount] = useState<number | undefined>(0);
  const formattedAmount = formatNumber(amount, 6, 6);
  const formattedAsaAmount =
    asaAmount !== undefined
      ? formatNumber(asaAmount, asaDecimals, asaDecimals)
      : "-";

  const [formError, setFormError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const highlightClass = useMemo(() => {
    return getHighlightClasses(listing.asaId, "text");
  }, [listing.asaId]);

  const { data: accountInfo, refetch: refetchAlgoAcct } =
    useAlgoAcctInfo(activeAddress);
  const userLstBalance = useBalance(lstId, accountInfo);

  const isInvalidAmount = asaAmount === undefined;
  const isLstOverspend = amount ? userLstBalance < amount : false;

  const enableActionButton =
    asaAmount !== undefined && !isLstOverspend && amount >= 1;
  const amountPreviewClass = enableActionButton ? "" : "text-muted-foreground";

  const changeAmount = ({
    currentTarget: { value: valueStr },
  }: React.ChangeEvent<HTMLInputElement>) => {
    const value = Math.round(Number(valueStr) * 1e6);
    if (value < 0) {
      setAmount(0);
      setAsaAmount(undefined);
      return;
    }
    if (valueStr == "" || value === 0) {
      setAmount(0);
      setAsaAmount(0);
      return;
    }
    const num = (Number(rate) * value) / Number(cState.ratePrecision);
    if (isNaN(num)) {
      setAmount(0);
      setAsaAmount(undefined);
    } else {
      setAmount(value);
      setAsaAmount(Math.floor(num));
    }
  };

  useEffect(() => {
    if (isLstOverspend) {
      setFormError(`Not enough ${listing.lstName}`);
    } else if (isInvalidAmount) {
      setFormError("Amount is invalid");
    } else {
      setFormError("");
    }
  }, [isLstOverspend, isInvalidAmount, asaAmount]);

  const onSuccess = () => {
    setAmount(0);
    setAsaAmount(0);
    inputRef.current!.value = "";
    refetchAlgoAcct();
    clearCStateCache(cState.appId);
    refetchHistoryEvents();
  };

  const setMax = (e: any) => {
    e.preventDefault();
    const value = userLstBalance;
    const valueStr = (value / 1e6).toString();
    const arg = {
      currentTarget: {
        value: valueStr,
      },
    } as unknown as React.ChangeEvent<HTMLInputElement>;
    changeAmount(arg);
    inputRef.current!.value = valueStr;
    return false;
  };

  return (
    <Card glowing="primary" className="w-[350px]">
      <CardHeader className="pb-4">
        <CardTitle className="font-normal -mx-5 text-center">
          <span className="whitespace-nowrap">
            Redeem{" "}
            <span className={`text-md font-bold ${highlightClass}`}>
              {lstName}
            </span>
          </span>
          <wbr />
          <span className="whitespace-nowrap">
            <ArrowRight size={16} className="inline -mt-1 mx-1" />
            <span className="text-md">ALGO+{asaUnitName}</span>
          </span>
        </CardTitle>
        <CardDescription className="flex flex-col text-muted-foreground/55">
          <span className="self-end">
            Lost?{" "}
            <Link to="https://docs.myth.finance/dualSTAKE/redeem">Read me</Link>
          </span>
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid w-full items-center gap-2">
          <div className="flex flex-col space-y-1.5">
            {listing?.sunset ? (
              <Alert className="border-orange-500 mb-2">
                <Sunset className="stroke-orange-500" />
                <AlertTitle className="font-bold ml-2 text-orange-500">
                  Sunset Warning
                </AlertTitle>
                <AlertDescription className="ml-2 text-sm">
                  {listing.lstName}{" "}
                  <Link to="https://docs.myth.finance/dualSTAKE/sunsetting">
                    has been sunset
                  </Link>
                  . Redeem all {listing.lstName} now.
                </AlertDescription>
              </Alert>
            ) : null}
            <Label
              className="flex justify-between items-center pt-1"
              htmlFor="redeem_amount"
            >
              <div className="flex items-center gap-2 text-muted-foreground">
                Amount
              </div>
              {activeAddress ? (
                <div className="flex items-center gap-1 text-muted-foreground">
                  Balance: {formatNumber(Math.abs(userLstBalance))}
                </div>
              ) : null}
            </Label>

            <div className="relative">
              <TooltipProvider>
                <Tooltip open={!!formError}>
                  <TooltipTrigger asChild>
                    <Input
                      id="redeem_amount"
                      autoComplete="off"
                      className={
                        isInvalidAmount || isLstOverspend
                          ? "focus-visible:ring-destructive"
                          : ""
                      }
                      onChange={changeAmount}
                      ref={inputRef}
                    />
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className={`bg-destructive`}>
                    {formError}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <div className="flex gap-1 items-center absolute top-[50%] right-1 -translate-y-[50%] text-sm text-muted-foreground pointer-events-none">
                <div className="mr-1">{lstName}</div>
                {activeAddress && userLstBalance > 0 ? (
                  <Button
                    onClick={setMax}
                    className={cn(
                      "py-[2px] px-5 -ml-1 text-xs rounded-md transition-colors h-min",
                      "bg-muted/30 hover:bg-muted/50 text-muted-foreground/90",
                      "focus:outline-none focus:ring-1 focus:ring-ring pointer-events-auto"
                    )}
                    size="icon"
                    variant="outline"
                  >
                    MAX
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
          <div className={amountPreviewClass}>
            <div className="flex justify-center items-center mt-4 space-y-1.5">
              <span className={enableActionButton ? "font-bold" : ""}>
                {enableActionButton || isLstOverspend ? formattedAmount : ""}{" "}
                ALGO
              </span>{" "}
            </div>
            <div className="flex items-center text-muted-foreground justify-between">
              <div>
                <ArrowRight size={16} />
              </div>
              <div
                className={cx(
                  "flex items-center",
                  enableActionButton ? "font-bold text-foreground" : ""
                )}
              >
                +
              </div>
              <div className="invisible">
                <ArrowRight size={16} />
              </div>
            </div>
            <div className="flex justify-center items-center space-y-1.5">
              <span className={enableActionButton ? "font-bold" : ""}>
                {asaAmount ? formattedAsaAmount : ""} {asaUnitName}
              </span>{" "}
            </div>
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex justify-center">
        <TransactionDialog
          type="redeem"
          trigger={(onClick) => (
            <ConnectButton onClick={onClick} disabled={!enableActionButton}>
              <HandCoins size={20} className="mr-1" /> Redeem ALGO+{asaUnitName}
            </ConnectButton>
          )}
          transactionBuilder={() =>
            makeRedeemTransactions(listing.appId, activeAddress!, amount)
          }
          title={
            <>
              <span className={highlightClass}>Redeem {lstName}</span>
            </>
          }
          onSuccess={onSuccess}
          // TODO onClose
        >
          {transactionInner(
            "redemption",
            <>
              You are going to{" "}
              <strong>
                redeem <span className={highlightClass}>{formattedAmount}</span>{" "}
                {lstName}
              </strong>
            </>,
            <>
              <div className="flex justify-between gap-2 mb-2">
                <div className="flex items-center">
                  <UserRound size={18} className="mr-1" /> Account
                </div>
                <span>{shorten(activeAddress!)}</span>
              </div>
              <div className="flex justify-between gap-2">
                <div className="flex items-center">
                  <ChevronRight size={18} className="mr-1" /> Depositing
                </div>
                <span>
                  {formattedAmount} {lstName}
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <div className="flex items-center">
                  <ChevronRight size={18} className="mr-1" />
                  App call
                </div>
                <span className="text-right text-muted-foreground">
                  App <ApplicationLink id={listing.appId} />
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <div className="flex items-center ml-3">
                  <ChevronLeft size={18} className="mr-1" /> Receiving
                </div>{" "}
                <span className="text-right">{formattedAmount} ALGO</span>
              </div>
              <div className="flex justify-between items-start gap-2">
                <div className="flex items-center ml-3">
                  <ChevronLeft size={18} className="mr-1" /> Receiving
                </div>
                <span className="text-right">
                  {formattedAsaAmount} {asaUnitName}
                </span>
              </div>
            </>
          )}
        </TransactionDialog>
      </CardFooter>
    </Card>
  );
}
